import { type RePricingImpactPreview } from "@kitchen/schemas";
import { Button, Chip, faDigits, formatMoney } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, financeApi, type TreeNode } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

export function RePricingTab() {
  const tree = useQuery({ queryKey: ["organizations"], queryFn: () => api<TreeNode[]>("/organizations") });

  const orgList: { id: string; name: string }[] = [];
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      orgList.push({ id: n.id, name: n.name });
      walk(n.children);
    }
  };
  walk(tree.data ?? []);

  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");

  const [previewResult, setPreviewResult] = useState<RePricingImpactPreview | null>(null);

  const previewAction = useAction(
    async ({ start, end, orgId }: { start: string; end: string; orgId?: string }) => {
      const res = await financeApi.previewRePricing({
        periodStart: start,
        periodEnd: end,
        organizationId: orgId || undefined,
      });
      setPreviewResult(res);
    },
    [],
  );

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Parameter Selection Panel */}
      <div className="panel" title="پیش‌نمایش اثر تغییرات نرخ‌گذاری بر فاکتورها">
        <p className="muted t-cap" style={{ marginBottom: "var(--s3)" }}>
          محاسبه اثر تغییر نرخ‌ها بر فاکتورهای پیش‌نویس و صادرشده در یک بازه زمانی. این عملیات صرفاً پیش‌نمایش
          است و هیچ سندی را ویرایش نمی‌کند.
        </p>

        <div className="row wrap" style={{ alignItems: "flex-end", gap: "var(--s3)" }}>
          <div>
            <label htmlFor="rp-org" className="label t-cap muted">
              سازمان (اختیاری)
            </label>
            <select
              id="rp-org"
              className="input"
              value={selectedOrgId}
              onChange={(e) => {
                setSelectedOrgId(e.target.value);
              }}
            >
              <option value="">همه سازمان‌ها</option>
              {orgList.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="rp-start" className="label t-cap muted">
              از تاریخ
            </label>
            <input
              id="rp-start"
              type="date"
              className="input"
              value={periodStart}
              onChange={(e) => {
                setPeriodStart(e.target.value);
              }}
            />
          </div>

          <div>
            <label htmlFor="rp-end" className="label t-cap muted">
              تا تاریخ
            </label>
            <input
              id="rp-end"
              type="date"
              className="input"
              value={periodEnd}
              onChange={(e) => {
                setPeriodEnd(e.target.value);
              }}
            />
          </div>

          <Button
            variant="primary"
            disabled={!periodStart || !periodEnd || previewAction.busy}
            onClick={() => {
              previewAction.run({ start: periodStart, end: periodEnd, orgId: selectedOrgId });
            }}
          >
            {previewAction.busy ? "در حال محاسبه..." : "محاسبه اثر تغییر قیمت"}
          </Button>
        </div>

        <ErrorLine error={previewAction.error} />
      </div>

      {/* Preview Impact Summary */}
      {previewResult && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "var(--s3)",
            }}
          >
            <div className="card">
              <span className="t-cap muted">پیش‌نویس‌های متأثر</span>
              <div className="bold t-title" style={{ marginTop: "4px" }}>
                {faDigits(previewResult.affectedDraftCount)} فاکتور
              </div>
              <p className="t-cap muted">تغییر مبلغ: {formatMoney(previewResult.draftsDiff, "rial")}</p>
            </div>

            <div className="card">
              <span className="t-cap muted">فاکتورهای صادرشده متأثر</span>
              <div className="bold t-title" style={{ marginTop: "4px" }}>
                {faDigits(previewResult.affectedIssuedCount)} فاکتور
              </div>
              <p className="t-cap muted">
                تغییر مبلغ: {formatMoney(previewResult.issuedRevisionsDiff, "rial")}
              </p>
            </div>

            <div className="card">
              <span className="t-cap muted">مابه‌التفاوت خالص کل</span>
              <div
                className="bold t-title"
                style={{
                  marginTop: "4px",
                  color:
                    previewResult.totalNetDifference > 0
                      ? "var(--blue-ink)"
                      : previewResult.totalNetDifference < 0
                        ? "var(--coral-strong)"
                        : undefined,
                }}
              >
                {previewResult.totalNetDifference > 0 ? "+" : ""}
                {formatMoney(previewResult.totalNetDifference, "rial")}
              </div>
              <p className="t-cap muted">
                {previewResult.totalNetDifference >= 0 ? "افزایش درآمد آشپزخانه" : "کاهش مبلغ صورتحساب‌ها"}
              </p>
            </div>
          </div>

          {/* Affected Invoices Table */}
          <div className="panel" title="فهرست فاکتورهای صادرشده مشمول تغییر">
            {previewResult.issuedRevisions.length === 0 ? (
              <p className="muted text-center" style={{ padding: "var(--s4) 0" }}>
                هیچ فاکتور صادرشده‌ای در این بازه دستخوش تغییر قیمت نشده است.
              </p>
            ) : (
              <table className="table" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>شماره فاکتور</th>
                    <th>ویرایش فعلی</th>
                    <th>مبلغ فعلی (ریال)</th>
                    <th>مبلغ پس از بازقیمت‌گذاری (ریال)</th>
                    <th>مابه‌التفاوت</th>
                    <th>نوع سند تعدیل</th>
                  </tr>
                </thead>
                <tbody>
                  {previewResult.issuedRevisions.map((inv) => (
                    <tr key={inv.invoiceId}>
                      <td className="bold">{faDigits(inv.invoiceNumber)}</td>
                      <td>ویرایش {faDigits(inv.currentRevision)}</td>
                      <td>{formatMoney(inv.oldTotal, "rial")}</td>
                      <td className="bold">{formatMoney(inv.newTotal, "rial")}</td>
                      <td
                        className="bold"
                        style={{
                          color: inv.diff > 0 ? "var(--blue-ink)" : "var(--coral-strong)",
                        }}
                      >
                        {inv.diff > 0 ? "+" : ""}
                        {formatMoney(inv.diff, "rial")}
                      </td>
                      <td>
                        <Chip tone={inv.noteType === "credit" ? "blue" : "warn"}>
                          {inv.noteType === "credit" ? "یادداشت بستانکار" : "یادداشت بدهکار"}
                        </Chip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="alert coral" style={{ marginTop: "var(--s4)", padding: "var(--s3)" }}>
              <span className="bold">قانون پلتفرم (AGENTS.md §3.7):</span> فاکتورهای صادرشده هرگز به‌صورت
              خودکار یا خاموش بازقیمت‌گذاری نمی‌شوند. برای اعمال نرخ جدید بر هر فاکتور صادرشده، مدیر باید از
              صفحه فاکتورها اقدام به ثبت «اصلاح فاکتور» نماید تا نوبت ویرایش جدید و یادداشت بدهکار/بستانکار
              ثبت گردد.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
